import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  MenuItem,
  InputLabel,
  FormControl,
  Select,
} from "@mui/material";

import axiosInstance from "../../utils/axiosInstance";
import appConfig from "../../config/appConfig";

const CustomerForm = ({
  open,
  onClose,
  initialValues = {},
  isUpdate = false,
  refreshCustomers,
  setSnackbar,
}) => {
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);

  // Reset form values when opening or switching between Add and Update
  useEffect(() => {
    if (!isUpdate) {
      // Reset form values for Add
      setFormValues({
        name: "",
        email: "",
        phone: "",
        status: "Inactive",
      });
    } else {
      // Populate form values for Update
      setFormValues({
        customerId: initialValues.customerId || "",
        name: initialValues.name || "",
        email: initialValues.email || "",
        phone: initialValues.phone || "",
        status: initialValues.status || "",
      });
    }
  }, [open, isUpdate, initialValues]);

  // Handle input changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (isUpdate) {
        // Update customer
        await axiosInstance.put(
          `${appConfig.endpoints.customers.byId(initialValues.customerId)}`,
          formValues,
        );
      } else {
        // Create customer
        await axiosInstance.post(
          appConfig.endpoints.customers.base,
          formValues,
        );
      }

      if (refreshCustomers) refreshCustomers(); // Refresh data on success

      // Display success message
      if (setSnackbar) {
        setSnackbar({
          open: true,
          message: isUpdate
            ? "Customer updated successfully!"
            : "Customer added successfully!",
          severity: "success",
        });
      }

      onClose(); // Close the modal
    } catch (error) {
      console.error("Error submitting customer data:", error);

      // Display error message
      if (setSnackbar) {
        setSnackbar({
          open: true,
          message:
            error.response?.data?.message || "Failed to submit customer data.",
          severity: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isUpdate ? "Update Customer" : "Add Customer"}</DialogTitle>
      <DialogContent>
        <TextField
          margin="dense"
          label="Name"
          type="text"
          fullWidth
          name="name"
          value={formValues.name}
          onChange={handleInputChange}
          required
        />
        <TextField
          margin="dense"
          label="Email"
          type="email"
          fullWidth
          name="email"
          value={formValues.email}
          onChange={handleInputChange}
          required
        />
        <TextField
          margin="dense"
          label="Phone"
          type="text"
          fullWidth
          name="phone"
          value={formValues.phone}
          onChange={handleInputChange}
          required
        />
        {isUpdate && (
          <FormControl fullWidth required>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              name="status"
              value={formValues.status || ""}
              onChange={handleInputChange}
            >
              <MenuItem value="Inactive">Inactive</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {isUpdate ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerForm;
